import React, { useState, useEffect, useCallback } from "react";
import {
  TextField, Button, Container, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Grid, Box, useMediaQuery, useTheme, Typography,
  Divider, Card, CardContent, Autocomplete, CircularProgress, Checkbox, FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DraftsIcon from "@mui/icons-material/Drafts"; 
import { useNavigate, useLocation } from "react-router-dom";
import {
  QuotationCreate, QuotationUpdate, GenerateQuotationNo,
  ApprovePartyList, getAllEnquiries,
  getCategories, fetchSubCategories, fetchItemsByCategoryAndSubcategory,
  getUnits, getDivisionUnits,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { debounce } from "lodash";

export default function FreightEntryForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  // ---------- ENQUIRY DROPDOWN ----------
  const [enquiryOptions, setEnquiryOptions] = useState([]);
  const [enquirySearch, setEnquirySearch] = useState("");
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  // ---------- PARTY ----------
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyPage, setPartyPage] = useState(1);
  const [partySearch, setPartySearch] = useState("");
  const [partyLoading, setPartyLoading] = useState(false);
  const [selectedPartyObj, setSelectedPartyObj] = useState(null);

  // ---------- MASTERS FOR ITEMS ----------
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [itemsMap, setItemsMap] = useState({});
  const [units, setUnits] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFinalAgreedEditable, setIsFinalAgreedEditable] = useState(false);

    const [isRevisedChecked, setIsRevisedChecked] = useState(false);

  const getSelectedDivision = () => {
    try { const s = localStorage.getItem("selectedDivision"); return s ? JSON.parse(s) : null; } catch { return null; }
  };
  const storedDivision = getSelectedDivision();

  const [formData, setFormData] = useState({
    id: null,
    ref_no: "",
    date: "",
    to_name: "",
    to_title: "",
    to_company: "",
    to_address: "",
    contact_no: "",
    email: "",
    subject: "Supply, Manufacturing, Erection & Commissioning of EOT Crane",
    body: "We are pleased to submit our quotation for the supply as per your inquiry.",
    validity: "",
    pf: "",
    transport_insurance: "",
    delivery: "",
    warranty: "",
    inspection_required: false,
    total_amount: 0,
    final_agreed_amount: 0,
    created_by: localStorage.getItem("user_id"),
    division: storedDivision?.division_id || "",
    party: "",
    contact_persons_list: [],
    enquiry_id: null,
    payment_terms: [
      { payment: "30 % interest-free advance along with the order. " },
      { payment: "30 % against drawing approval & Inspection of Raw material at our workshop. " },
      { payment: "40% Balance against receipt of Performa invoice Before Material Dispatch." },
    ],
    terms_and_conditions: [
      { key: "GST", value: "@ 18% EXTRA  ", is_default: true },
      { key: "Validity", value: "10 Days", is_default: true },
      { key: "P & F", value: "EXCLUSIVE", is_default: true },
      { key: "Transportation and Insurance", value: "To Be arranged by Purchaser", is_default: true },
      { key: "Delivery", value: "14 - 16 Weeks from advance/drawing approval.", is_default: true },
      { 
        key: "Bank Details", 
        value: "CRANOIST MATERIAL HANDLING EQUIPMENTS PVT. LTD.\nBANK: BANK OF MAHARASHTRA,\nA/C: 60493771870\nIFSC: MAHB0000057\nBRANCH NAME: RK HEIGHTS, DURG ", 
        is_default: true 
      },
    ],
    items: [{ category: null, sub_category: null, item: null, description: "", unit: "", quantity: 0, rate: 0, amount: 0 }],
  });

  // ---------- FETCH MASTERS ----------
  useEffect(() => {
    const init = async () => {
      try {
        const catRes = await getCategories();
        setCategories(Array.isArray(catRes) ? catRes : catRes?.results || []);
        try {
          const divData = await getDivisionUnits();
          setUnits(divData.units || []);
        } catch {
          const u = await getUnits();
          setUnits(u.results || u.units || u || []);
        }
        const enqData = await getAllEnquiries();
        setEnquiryOptions(Array.isArray(enqData) ? enqData : enqData?.results || []);
      } catch (e) { console.error(e); }
    };
    init();
  }, []);

  // ---------- FETCH ENQUIRY SEARCH ----------
  const fetchEnquiries = async (search = "") => {
    setEnquiryLoading(true);
    try {
      const data = await getAllEnquiries();
      let list = Array.isArray(data) ? data : data?.results || [];
      if (search) {
        const s = search.toLowerCase();
        list = list.filter(en =>
          (en.enquiry_no || "").toLowerCase().includes(s) ||
          (en.customer_name || "").toLowerCase().includes(s) ||
          (en.company_name || "").toLowerCase().includes(s)
        );
      }
      setEnquiryOptions(list);
    } catch { } finally { setEnquiryLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { fetchEnquiries(enquirySearch); }, 400);
    return () => clearTimeout(t);
  }, [enquirySearch]);

  // ---------- FETCH PARTY ----------
  const fetchNextQuotationNo = async () => {
    try {
      const data = await GenerateQuotationNo();
      const refNo = data?.ref_no || data?.data?.ref_no;
      if (refNo) {
        setFormData(prev => ({ ...prev, ref_no: refNo }));
      }
    } catch (err) {
      console.error("Error fetching Quotation No:", err);
    }
  };

  const getParties = async (pageNum = 1, filters = {}) => {
    try {
      setPartyLoading(true);
      const response = await ApprovePartyList(pageNum, filters);
      const results = Array.isArray(response) ? response : [];
      setPartyOptions(prev => pageNum === 1 ? results : [...prev, ...results]);
      setPartyPage(pageNum);
    } catch { } finally { setPartyLoading(false); }
  };

  useEffect(() => { 
    getParties(1, { name: "" }); 
  }, []);

  const quoteData = location.state?.data ?? null;
  const passedSelectedEnquiry = location.state?.selectedEnquiry ?? null;
  const passedEnquiryId = location.state?.data?.enquiry_id || location.state?.data?.enquiry || null;

  useEffect(() => {
    if (!quoteData) {
      fetchNextQuotationNo();
    }
  }, [quoteData]);

  useEffect(() => { const timer = setTimeout(() => getParties(1, { name: partySearch }), 500); return () => clearTimeout(timer); }, [partySearch]);

  useEffect(() => {
    if (!quoteData) return;
    setIsEditMode(true);
    const normalizedItems = Array.isArray(quoteData.items_list) && quoteData.items_list.length > 0
      ? quoteData.items_list.map(it => ({
        category: it.category ? { id: it.category, name: it.category_name || "" } : null,
        sub_category: it.sub_category ? { subcat_id: it.sub_category, name: it.sub_category_name || "" } : null,
        item: it.item ? { id: it.item, name: it.item_name || "", rate: it.rate || 0 } : null,
        description: it.description || it.item_name || "",
        quantity: parseFloat(it.quantity) || 0,
        unit: it.unit || "",
        rate: parseFloat(it.rate) || 0,
        amount: parseFloat(it.amount) || 0,
      }))
      : [{ category: null, sub_category: null, item: null, description: "", quantity: 0, rate: 0, amount: 0, unit: "" }];
    
    setFormData(prev => ({
      ...prev,
      id: quoteData.quotation_id,
      ref_no: quoteData.ref_no,
      date: quoteData.date || "",
      to_name: quoteData.to_name || "",
      to_title: quoteData.to_title || "",
      to_company: quoteData.to_company || "",
      to_address: quoteData.to_address || "",
      contact_no: quoteData.contact_no || "",
      email: quoteData.email || "",
      subject: quoteData.subject || prev.subject,
      body: quoteData.body || prev.body,
      total_amount: quoteData.total_amount || 0,
      final_agreed_amount: quoteData.final_agreed_amount || 0,
      payment_terms: quoteData.payment_terms || prev.payment_terms,
      terms_and_conditions: quoteData.terms_and_conditions || prev.terms_and_conditions,
      items: normalizedItems,
      party: quoteData.party?.id || quoteData.party || "",
    }));

    if (passedSelectedEnquiry) {
      setSelectedEnquiry(passedSelectedEnquiry);
    }
    setFormData(prev => ({
      ...prev,
      enquiry_id: passedEnquiryId || prev.enquiry_id,
    }));
  }, [quoteData]);

  useEffect(() => {
    if (partyOptions.length > 0 && formData.to_company && !formData.party) {
      const matched = partyOptions.find(o => 
        String(o.company_name).toLowerCase() === String(formData.to_company).toLowerCase()
      );
      if (matched) {
        setFormData(prev => ({ ...prev, party: matched.id }));
        setSelectedPartyObj(matched);
      }
    }
  }, [partyOptions, formData.to_company, formData.party]);

  useEffect(() => {
    if (isEditMode && partyOptions.length > 0 && formData.party) {
      const matchedParty = partyOptions.find(o => String(o.id) === String(formData.party));
      if (matchedParty) {
        let unifiedContacts = [];
        if (matchedParty.name) {
          unifiedContacts.push({
            title: "",
            contact_name: matchedParty.name,
            contact_no: matchedParty.contact || "",
            designation: "Primary Contact",
          });
        }
        const additionalContacts = (matchedParty.contact_persons || []).filter(
          (ap) => ap.contact_name?.toLowerCase() !== matchedParty.name?.toLowerCase()
        );
        const mergedList = [...unifiedContacts, ...additionalContacts];
        setFormData(prev => ({ ...prev, contact_persons_list: mergedList }));
        setSelectedPartyObj(matchedParty);
      }
    }
  }, [isEditMode, partyOptions, formData.party]);

  // ---------- WHEN ENQUIRY SELECTED -> AUTO FILL ----------
  const handleEnquirySelect = async (enq) => {
    setSelectedEnquiry(enq);
    if (!enq) {
      setFormData(prev => ({ ...prev, enquiry_id: null, items: [{ category: null, sub_category: null, item: null, description: "", quantity: 0, rate: 0, amount: 0, unit: "" }] }));
      return;
    }

    const srcItems = enq.line_items || enq.items || [];
    const mapped = srcItems.map(it => ({
      category: it.category ? { id: it.category, name: it.category_name || it.category } : (it.category_name ? { id: it.category, name: it.category_name } : null),
      sub_category: it.sub_category ? { subcat_id: it.sub_category, name: it.sub_category_name || "" } : null,
      item: it.item ? { id: it.item, name: it.item_name || it.description, rate: it.rate || 0 } : (it.item_name ? { id: it.item, name: it.item_name, rate: it.rate || 0 } : null),
      description: it.description || it.item_name || "",
      quantity: parseFloat(it.quantity) || 0,
      unit: it.unit || it.unit_name || "",
      rate: parseFloat(it.rate) || 0,
      amount: parseFloat(it.amount) || (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0),
    }));

    for (let idx = 0; idx < mapped.length; idx++) {
      const row = mapped[idx];
      if (row.category?.id) {
        try {
          const subcats = await fetchSubCategories(row.category.id);
          setSubcategories(prev => { const c = [...prev]; c[idx] = subcats.results || subcats || []; return c; });
          const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, 0, 1, "");
          const list = (itemData.results || itemData || []).map(i => ({
            ...i,
            id: i.id,
            name: i.name,
            description: i.description || i.name || "",
            unit_id: typeof i.unit === "object" ? i.unit?.id : i.unit || i.uom || null,
            unit_name: typeof i.unit === "object" ? i.unit?.name : i.unit_name || i.uom_name || "",
            rate: i.rate || i.unit_price || i.selling_price || i.price || 0
          }));
          setItemsMap(prev => ({ ...prev, [idx]: list }));
        } catch { }
      }
    }

    const total = mapped.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const matchedParty = partyOptions.find(p => p.company_name === enq.company_name);

    let unifiedContacts = [];
    if (matchedParty) {
      if (matchedParty.name) {
        unifiedContacts.push({
          title: "",
          contact_name: matchedParty.name,
          contact_no: matchedParty.contact || "",
          designation: "Primary Contact",
        });
      }
      const additionalContacts = (matchedParty.contact_persons || []).filter(
        (cp) => cp.contact_name?.toLowerCase() !== matchedParty.name?.toLowerCase()
      );
      unifiedContacts = [...unifiedContacts, ...additionalContacts];
    }

    setFormData(prev => ({
      ...prev,
      enquiry_id: enq.enquiry_id || enq.id,
      to_company: enq.company_name || "",
      to_name: enq.customer_name || "",
      email: enq.email || "",
      contact_no: enq.phone || "",
      to_address: enq.address || "",
      party: matchedParty ? matchedParty.id : prev.party,
      contact_persons_list: unifiedContacts,
      items: mapped.length > 0 ? mapped : prev.items,
      total_amount: total,
    }));
    toast.success(`Enquiry ${enq.enquiry_no} loaded - ${mapped.length} items`);
  };

  // ══════════════════════════════════════════════════════════════════
  // 🟢 AUTO FILL UNIT (UOM) & UNIT PRICE (RATE) ON ITEM SELECT
  // ══════════════════════════════════════════════════════════════════
  const handleItemAutocompleteChange = async (index, key, value) => {
    const updated = [...formData.items];
    let row = { ...updated[index] };
    switch (key) {
      case "category":
        row = { 
          ...row, 
          category: value, 
          sub_category: null, 
          item: null, 
          description: "", 
          rate: "", 
          amount: 0 
        };
        if (value) {
          const subcats = await fetchSubCategories(value.id);
          setSubcategories(prev => { const c = [...prev]; c[index] = subcats.results || subcats || []; return c; });
          const itemData = await fetchItemsByCategoryAndSubcategory(value.id, 0, 1, "");
          const list = (itemData.results || itemData || []).map(i => ({
            ...i,
            id: i.id,
            name: i.name,
            description: i.description || i.name || "",
            unit_id: typeof i.unit === "object" ? i.unit?.id : i.unit || i.uom || null,
            unit_name: typeof i.unit === "object" ? i.unit?.name : i.unit_name || i.uom_name || "",
            rate: i.rate || i.unit_price || i.selling_price || i.price || 0
          }));
          setItemsMap(prev => ({ ...prev, [index]: list }));
        } else {
          setItemsMap(prev => ({ ...prev, [index]: [] }));
        }
        break;

      case "sub_category":
        row = { ...row, sub_category: value, item: null, rate: "", amount: 0 };
        if (value && row.category) {
          const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, value.subcat_id || value.id, 1, "");
          const list = (itemData.results || itemData || []).map(i => ({
            ...i,
            id: i.id,
            name: i.name,
            description: i.description || i.name || "",
            unit_id: typeof i.unit === "object" ? i.unit?.id : i.unit || i.uom || null,
            unit_name: typeof i.unit === "object" ? i.unit?.name : i.unit_name || i.uom_name || "",
            rate: i.rate || i.unit_price || i.selling_price || i.price || 0
          }));
          setItemsMap(prev => ({ ...prev, [index]: list }));
        } else {
          setItemsMap(prev => ({ ...prev, [index]: [] }));
        }
        break;

      case "item":
        const autoRate = parseFloat(value?.rate || value?.unit_price || value?.selling_price || value?.price || 0) || 0;
        const autoUnitName = typeof value?.unit === "object" ? value?.unit?.name : value?.unit_name || value?.unit || row.unit || "";
        
        row = { 
          ...row, 
          item: value, 
          description: value?.description || value?.name || "", 
          unit: autoUnitName,
          rate: autoRate > 0 ? autoRate : "", 
        };
        break;

      case "unit":
        row = { ...row, unit: value?.id || value?.name || value || "" };
        break;
    }

    const qty = parseFloat(row.quantity) || 0;
    const rate = parseFloat(row.rate) || 0;
    row.amount = qty * rate;
    
    updated[index] = row;
    const total = updated.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    setFormData(prev => ({ ...prev, items: updated, total_amount: total }));
  };

  const handleItemInputChange = useCallback(debounce(async (index, row, newValue, type) => {
    if (type === "category") {
      const catData = await getCategories(1, newValue || "");
      setCategories(Array.isArray(catData.results) ? catData.results : catData);
    }
    if (type === "item" && row.category) {
      const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, row.sub_category?.subcat_id || 0, 1, newValue || "");
      const list = (itemData.results || []).map(i => ({
        ...i,
        id: i.id,
        name: i.name,
        description: i.description || i.name || "",
        unit_id: typeof i.unit === "object" ? i.unit?.id : i.unit || i.uom || null,
        unit_name: typeof i.unit === "object" ? i.unit?.name : i.unit_name || i.uom_name || "",
        rate: i.rate || i.unit_price || i.selling_price || i.price || 0
      }));
      setItemsMap(prev => ({ ...prev, [index]: list }));
    }
  }, 400), []);

  const handleItemChange = (e, index) => {
    const { name, value } = e.target;
    const updated = [...formData.items];
    updated[index][name] = name === "description" || name === "unit" ? value : (value === "" ? "" : Number(value));
    if (name === "quantity" || name === "rate") {
      updated[index].amount = (Number(updated[index].quantity) || 0) * (Number(updated[index].rate) || 0);
    }
    const total = updated.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    setFormData(prev => ({ ...prev, items: updated, total_amount: total }));
  };

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, { category: null, sub_category: null, item: null, description: "", quantity: 0, rate: 0, amount: 0, unit: "" }] }));
  
  const deleteItem = (idx) => {
    const upd = formData.items.filter((_, i) => i !== idx);
    const total = upd.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    setFormData(prev => ({ ...prev, items: upd, total_amount: total }));
  };

  // ---------- OTHER HANDLERS ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === "contact_no") v = value.replace(/\D/g, "").slice(0, 10);
    if (name === "final_agreed_amount") v = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [name]: v }));
  };

  const handleTermChange = (idx, field, val) => {
    const terms = [...formData.terms_and_conditions]; terms[idx][field] = val;
    setFormData(prev => ({ ...prev, terms_and_conditions: terms }));
  };

  const addTerm = () => setFormData(prev => ({ ...prev, terms_and_conditions: [...prev.terms_and_conditions, { key: "", value: "", is_default: false }] }));
  
  const removeTerm = (idx) => setFormData(prev => ({ ...prev, terms_and_conditions: prev.terms_and_conditions.filter((_, i) => i !== idx) }));

  // ---------- PAYMENT TERMS HANDLERS ----------
  const handlePaymentTermChange = (idx, value) => {
    const updated = [...(formData.payment_terms || [])];
    if (updated[idx]) {
      updated[idx].payment = value;
    }
    setFormData(prev => ({ ...prev, payment_terms: updated }));
  };

  const addPaymentTerm = () => {
    setFormData(prev => ({
      ...prev,
      payment_terms: [...(prev.payment_terms || []), { payment: "" }]
    }));
  };

  const removePaymentTerm = (idx) => {
    setFormData(prev => ({
      ...prev,
      payment_terms: (prev.payment_terms || []).filter((_, i) => i !== idx)
    }));
  };
  // ---------- REVISED CHECKBOX HANDLER ----------
  const handleRevisedToggle = (e) => {
    const checked = e.target.checked;
    setIsRevisedChecked(checked);

    setFormData((prev) => {
      let newRef = prev.ref_no || "";
      if (checked) {
        // Agar check kiya, toh /Q- ko /QR- banao
        newRef = newRef.replace(/\/Q-/i, "/RQ-");
      } else {
        // Agar uncheck kiya, toh wapas /QR- ko /Q- banao
        newRef = newRef.replace(/\/RQ-/i, "/Q-");
      }
      return { ...prev, ref_no: newRef };
    });
  };
  // ---------- DRAFT SAVE ----------
  const handleSaveDraft = () => {
    const draftData = {
      formData,
      selectedEnquiry,
      selectedPartyObj,
    };
    sessionStorage.setItem("quotationDraft", JSON.stringify(draftData));
    toast.info("Quotation draft saved locally!");
  };

  // ---------- DRAFT RESTORE ----------
  useEffect(() => {
    const savedDraft = sessionStorage.getItem("quotationDraft");
    if (savedDraft && !isEditMode) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.formData) setFormData(parsed.formData);
        if (parsed.selectedEnquiry) setSelectedEnquiry(parsed.selectedEnquiry);
        if (parsed.selectedPartyObj) setSelectedPartyObj(parsed.selectedPartyObj);

        if (parsed.formData?.items) {
          parsed.formData.items.forEach(async (row, idx) => {
            if (row.category?.id) {
              try {
                const subcats = await fetchSubCategories(row.category.id);
                setSubcategories(prev => { const c = [...prev]; c[idx] = subcats.results || subcats || []; return c; });
                const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, row.sub_category?.subcat_id || 0, 1, "");
                const list = (itemData.results || itemData || []).map(i => ({
                  ...i,
                  id: i.id,
                  name: i.name,
                  description: i.description || i.name || "",
                  unit_id: typeof i.unit === "object" ? i.unit?.id : i.unit || i.uom || null,
                  unit_name: typeof i.unit === "object" ? i.unit?.name : i.unit_name || i.uom_name || "",
                  rate: i.rate || i.unit_price || i.selling_price || i.price || 0
                }));
                setItemsMap(prev => ({ ...prev, [idx]: list }));
              } catch (err) {
                console.error("Restoring Draft Category options error:", err);
              }
            }
          });
        }
        toast.info("Loaded Quotation draft!");
      } catch (e) {
        console.error("Quotation Draft parse error:", e);
      }
    }
  }, [isEditMode]);

  const handleQuotationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanedItems = formData.items.map(it => ({
        category: it.category?.id || it.category || null,
        sub_category: it.sub_category?.subcat_id || it.sub_category?.id || it.sub_category || null,
        item: it.item?.id || it.item || null,
        description: it.description || it.item?.name || "",
        quantity: parseFloat(it.quantity) || 0,
        unit: it.unit?.id || it.unit || it.unit?.name || "",
        rate: parseFloat(it.rate) || 0,
        amount: parseFloat(it.amount) || 0,
      }));
      const payload = {
        ...formData,
        created_by: formData.created_by || localStorage.getItem("user_id"),
        division: storedDivision?.division_id || "",
        items: cleanedItems,
        items_list: cleanedItems,
        enquiry: formData.enquiry_id || null,
        to_title: formData.to_title || "",
        final_agreed_amount: formData.final_agreed_amount || 0,
        party: formData.party || null,
      };
      if (isEditMode) {
        await QuotationUpdate(payload.id, payload);
        toast.success("Quotation updated!", { onClose: () => navigate("/dashboard/quotation"), autoClose: 1500 });
      } else {
        await QuotationCreate(payload);
        toast.success("Quotation created!", { onClose: () => navigate("/dashboard/quotation"), autoClose: 1500 });
      }
      sessionStorage.removeItem("quotationDraft");
    } catch (err) { console.error(err); toast.error("Failed"); } finally { setLoading(false); }
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 2 }, py: 2 }}>
      <ToastContainer />
      <form onSubmit={handleQuotationSubmit} autoComplete="off">
        {/* BUTTONS HEADER BAR */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h5" fontWeight="bold">{isEditMode ? "Edit Quotation" : "Create Quotation"}</Typography>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            
            {!isEditMode && (
              <Button
                type="button"
                variant="outlined"
                startIcon={<DraftsIcon />}
                onClick={handleSaveDraft}
                sx={{
                  height: 36,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#ff8400",
                  borderColor: "#ff8400",
                  "&:hover": { borderColor: "#e67300", bgcolor: "#fff3e0" },
                }}
              >
                Save Draft
              </Button>
            )}

            {/* 🟢 NAYA REVISED CHECKBOX YAHAN HAI */}
            {isEditMode && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRevisedChecked}
                    onChange={handleRevisedToggle}
                    color="warning"
                    size="small"
                  />
                }
                label={<Typography sx={{ fontWeight: "bold", color: "#d84315", fontSize: "0.85rem" }}>Mark as Revised</Typography>}
                sx={{ mr: 1, border: "1px solid #ffcc80", bgcolor: "#fff3e0", pr: 1.5, borderRadius: 1 }}
              />
            )}

            <Button type="submit" variant="contained" disabled={loading} sx={{ height: 36, textTransform: "none" }}>
              {loading ? <CircularProgress size={20} /> : isEditMode ? "Update" : "Submit"}
            </Button>
          </Box>
        </Box>

        {/* ENQUIRY DROPDOWN */}
        <Paper sx={{ p: 2, mb: 2, border: "2px solid #1976d2", bgcolor: "#f0f7ff" }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: "#1976d2" }}>🔗 Select Enquiry to Auto-Fill (Optional)</Typography>
          <Autocomplete
            options={enquiryOptions}
            getOptionLabel={(opt) => `${opt.enquiry_no || ""} - ${opt.customer_name || ""} / ${opt.company_name || ""} (${(opt.line_items?.length || opt.items?.length || 0)} items)`}
            value={selectedEnquiry}
            loading={enquiryLoading}
            onChange={(e, val) => handleEnquirySelect(val)}
            onInputChange={(e, v, reason) => { if (reason === "input") setEnquirySearch(v); }}
            isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
            renderInput={(params) => (
              <TextField {...params} label="Search Enquiry No / Customer / Company" size="small"
                InputProps={{ ...params.InputProps, endAdornment: <>{enquiryLoading && <CircularProgress size={18} />}{params.InputProps.endAdornment}</> }} />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.enquiry_id}>
                <Box><Typography variant="body2" fontWeight="bold">{option.enquiry_no} - {option.customer_name}</Typography><Typography variant="caption">{option.company_name} | Qty: {(option.line_items?.length || 0)} | {option.city}</Typography></Box>
              </li>
            )}
          />
          {selectedEnquiry && <Typography variant="caption" sx={{ mt: 1, display: "block", color: "green" }}>✅ Enquiry {selectedEnquiry.enquiry_no} loaded. You can still add/edit items manually.</Typography>}
        </Paper>

        <Box border="1px solid #ccc" borderRadius="8px" mb={2} p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Quotation Ref No" name="ref_no" value={formData.ref_no} disabled /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Date" name="date" type="date" value={formData.date} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={4}>
              {/* ✅ FIX 2: Autocomplete value logic matches by either Party ID OR to_company Name */}
              <Autocomplete 
                options={partyOptions} 
                getOptionLabel={(o) => o.company_name || ""} 
                value={partyOptions.find(o => 
                  (formData.party && String(o.id) === String(formData.party)) ||
                  (formData.to_company && String(o.company_name).toLowerCase() === String(formData.to_company).toLowerCase())
                ) || null}
                onChange={(e, v) => {
                  if (v) {
                    let unifiedContacts = [];
                    if (v.name) {
                      unifiedContacts.push({
                        title: "",
                        contact_name: v.name,
                        contact_no: v.contact || "",
                        designation: "Primary Contact",
                      });
                    }
                    const additionalContacts = (v.contact_persons || []).filter(
                      (cp) => cp.contact_name?.toLowerCase() !== v.name?.toLowerCase()
                    );
                    const mergedList = [...unifiedContacts, ...additionalContacts];

                    const defaultCP = mergedList[0] || {};
                    const defaultName = defaultCP.contact_name ? `${defaultCP.title || ""} ${defaultCP.contact_name}`.trim() : "";

                    setFormData(p => ({
                      ...p,
                      party: v.id,
                      to_company: v.company_name || "",
                      to_name: defaultName,
                      to_title: defaultCP.title || "",
                      to_address: v.address || "",
                      contact_no: defaultCP.contact_no || v.contact || "",
                      email: v.email || "",
                      contact_persons_list: mergedList
                    }));
                    setSelectedPartyObj(v);
                  } else {
                    setFormData(p => ({
                      ...p,
                      party: "",
                      to_company: "",
                      to_name: "",
                      to_title: "",
                      to_address: "",
                      contact_no: "",
                      email: "",
                      contact_persons_list: []
                    }));
                    setSelectedPartyObj(null);
                  }
                }}
                onInputChange={(e, v, r) => { if (r === "input") setPartySearch(v); }}
                isOptionEqualToValue={(option, val) => String(option?.id) === String(val?.id)}
                renderInput={(params) => <TextField {...params} label="Company Name" size="small" required InputProps={{ ...params.InputProps, endAdornment: <>{partyLoading && <CircularProgress size={18} />}{params.InputProps.endAdornment}</> }} />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete options={formData.contact_persons_list || []} 
                getOptionLabel={(o) => {
                  const title = o.title || "";
                  const name = o.contact_name || "";
                  const designation = o.designation ? `(${o.designation})` : "";
                  return `${title} ${name} ${designation}`.trim();
                }} 
                value={formData.contact_persons_list?.find(p => {
                  const fn = `${p.title || ""} ${p.contact_name || ""}`.trim();
                  return fn === formData.to_name;
                }) || null}
                onChange={(e, v) => {
                  if (v) {
                    setFormData(p => ({
                      ...p,
                      to_name: `${v.title || ""} ${v.contact_name || ""}`.trim(),
                      contact_no: v.contact_no || p.contact_no,
                      to_title: v.title || "",
                      email: v.email || p.email
                    }));
                  }
                }}
                isOptionEqualToValue={(option, val) => option.contact_name === val?.contact_name}
                renderInput={(params) => <TextField {...params} label="To Name" size="small" required />}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: "bold" }}>
                        {option.title} {option.contact_name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
                        📞 {option.contact_no || "N/A"} | 💼 {option.designation || "N/A"}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Contact No." name="contact_no" value={formData.contact_no} onChange={handleChange} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Email" name="email" value={formData.email} onChange={handleChange} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="To Address" name="to_address" value={formData.to_address} onChange={handleChange} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Subject" name="subject" value={formData.subject} onChange={handleChange} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Body" name="body" value={formData.body} onChange={handleChange} multiline rows={2} /></Grid>
          </Grid>
        </Box>

        {/* ITEMS - CATEGORY WISE */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">📦 Quotation Items (Category wise - Enquiry se auto)</Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addItem}>Add Item</Button>
          </Box>
          {isMobile ? (
            <Box>
              {formData.items.map((row, idx) => (
                <Card key={idx} sx={{ mb: 1, p: 1 }}>
                  <CardContent sx={{ p: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography variant="subtitle2">#{idx + 1}</Typography><IconButton size="small" color="error" onClick={() => deleteItem(idx)}><DeleteIcon /></IconButton></Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                      <Autocomplete options={categories} getOptionLabel={o => o.name || ""} value={row.category || null} onChange={(e, v) => handleItemAutocompleteChange(idx, "category", v)} onInputChange={(e, v, r) => { if (r === "input") handleItemInputChange(idx, row, v, "category"); }} isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)} renderInput={p => <TextField {...p} label="Category" size="small" />} />
                      <Autocomplete options={itemsMap[idx] || []} getOptionLabel={o => o.name || ""} value={row.item || null} onChange={(e, v) => handleItemAutocompleteChange(idx, "item", v)} onInputChange={(e, v) => handleItemInputChange(idx, row, v, "item")} isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)} renderInput={p => <TextField {...p} label="Item" size="small" />} />
                      
                      {/* ✅ FIX 3 (Mobile): Matching logic + asynchronous temporary mock object fallback */}
                      <Autocomplete 
                        options={units} 
                        getOptionLabel={o => o.name || o.unit_name || o} 
                        value={units.find(u => 
                          String(u.id) === String(row.unit) || 
                          String(u.name).toLowerCase() === String(row.unit).toLowerCase()
                        ) || (row.unit ? { id: row.unit, name: row.unit } : null)} 
                        onChange={(e, v) => { const up = [...formData.items]; up[idx].unit = v?.id || v?.name || ""; setFormData(p => ({ ...p, items: up })); }} 
                        isOptionEqualToValue={(option, val) => String(option?.id) === String(val?.id) || String(option?.name) === String(val)} 
                        renderInput={p => <TextField {...p} size="small" label="Unit" />} 
                      />
                      
                      <TextField label="Description" size="small" fullWidth name="description" value={row.description} onChange={e => handleItemChange(e, idx)} />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <TextField label="Qty" size="small" type="number" value={row.quantity} onChange={e => handleItemChange(e, idx)} name="quantity" fullWidth />
                        <TextField label="Rate" size="small" type="number" value={row.rate} onChange={e => handleItemChange(e, idx)} name="rate" fullWidth />
                      </Box>
                      <TextField label="Amount" size="small" value={row.amount} disabled fullWidth />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ tableLayout: "fixed", minWidth: 1100 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                    <TableCell sx={{ width: "15%" }}>Category</TableCell>
                    <TableCell sx={{ width: "17%" }}>Item</TableCell>
                    <TableCell sx={{ width: "11%" }}>Unit</TableCell>
                    <TableCell sx={{ width: "22%" }}>Description</TableCell>
                    <TableCell sx={{ width: "8%" }}>Qty</TableCell>
                    <TableCell sx={{ width: "10%" }}>Rate</TableCell>
                    <TableCell sx={{ width: "12%" }}>Amount</TableCell>
                    <TableCell sx={{ width: "5%" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((row, idx) => (
                    <TableRow key={idx}>
                      {/* Category */}
                      <TableCell>
                        <Autocomplete
                          options={categories}
                          getOptionLabel={o => o.name || ""}
                          value={row.category || null}
                          onChange={(e, v) => handleItemAutocompleteChange(idx, "category", v)}
                          onInputChange={(e, v, r) => { if (r === "input") handleItemInputChange(idx, row, v, "category"); }}
                          isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                          renderInput={p => <TextField {...p} size="small" label="Category" />}
                        />
                      </TableCell>

                      {/* Item */}
                      <TableCell>
                        <Autocomplete
                          options={itemsMap[idx] || []}
                          getOptionLabel={o => o.name || ""}
                          value={row.item || null}
                          onChange={(e, v) => handleItemAutocompleteChange(idx, "item", v)}
                          onInputChange={(e, v) => handleItemInputChange(idx, row, v, "item")}
                          isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                          renderInput={p => <TextField {...p} size="small" label="Item" />}
                        />
                      </TableCell>

                      {/* Unit */}
                      <TableCell>
                        {/* ✅ FIX 4 (Desktop Table): Matching logic + asynchronous temporary mock object fallback */}
                        <Autocomplete
                          options={units}
                          getOptionLabel={o => o.name || o.unit_name || ""}
                          value={units.find(u => 
                            String(u.id) === String(row.unit) || 
                            String(u.name).toLowerCase() === String(row.unit).toLowerCase()
                          ) || (row.unit ? { id: row.unit, name: row.unit } : null)}
                          onChange={(e, v) => {
                            const up = [...formData.items];
                            up[idx].unit = v?.id || v?.name || "";
                            setFormData(p => ({ ...p, items: up }));
                          }}
                          isOptionEqualToValue={(option, val) => String(option?.id) === String(val?.id) || String(option?.name) === String(val)}
                          renderInput={p => <TextField {...p} size="small" label="Unit" />}
                        />
                      </TableCell>

                      {/* Description */}
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          name="description"
                          value={row.description}
                          onChange={e => handleItemChange(e, idx)}
                        />
                      </TableCell>

                      {/* Qty */}
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          name="quantity"
                          value={row.quantity}
                          onChange={e => handleItemChange(e, idx)}
                          fullWidth
                        />
                      </TableCell>

                      {/* Rate */}
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          name="rate"
                          value={row.rate}
                          onChange={e => handleItemChange(e, idx)}
                          fullWidth
                        />
                      </TableCell>

                      {/* Amount */}
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.amount}
                          disabled
                          fullWidth
                        />
                      </TableCell>

                      {/* Action */}
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => deleteItem(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={6} align="right"><strong>Total</strong></TableCell>
                    <TableCell><strong>₹ {formData.total_amount.toFixed(2)}</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* TERMS & CONDITIONS & PAYMENT TERMS */}
        <Box border="1px solid #ccc" borderRadius="8px" p={2} mt={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">⚙️ Terms & Conditions</Typography>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addTerm}>
              Add Term
            </Button>
          </Box>

          {formData.terms_and_conditions.map((term, idx) => (
            <Grid container spacing={1} key={idx} sx={{ mb: 1.5 }} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Term Key"
                  value={term.key}
                  onChange={e => handleTermChange(idx, "key", e.target.value)}
                />
              </Grid>
              <Grid item xs={10} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Value"
                  value={term.value}
                  onChange={e => handleTermChange(idx, "value", e.target.value)}
                  multiline={term.key === "Bank Details" || term.key?.toLowerCase().includes("bank")}
                  minRows={term.key === "Bank Details" ? 5 : 1}
                  maxRows={6}
                />
              </Grid>
              <Grid item xs={2} sm={1} sx={{ textAlign: "center" }}>
                <IconButton size="small" color="error" onClick={() => removeTerm(idx)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          {/* EDITABLE PAYMENT TERMS */}
          <Box sx={{ mt: 3, pt: 2, borderTop: "1px dashed #bbb" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#1976d2" }}>
                💰 Payment Terms (Bullet points for Print)
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addPaymentTerm}
                sx={{ textTransform: "none" }}
              >
                Add Payment Term
              </Button>
            </Box>

            {(formData.payment_terms || []).length === 0 && (
              <Typography variant="body2" sx={{ color: "#888", fontStyle: "italic", mb: 1 }}>
                No payment terms added. Click "Add Payment Term" to write terms.
              </Typography>
            )}

            {(formData.payment_terms || []).map((pt, idx) => (
              <Grid container spacing={1} key={idx} sx={{ mb: 1.5 }} alignItems="center">
                <Grid item xs={1} sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: "bold", color: "#666" }}>
                    {idx + 1}.
                  </Typography>
                </Grid>
                <Grid item xs={9} sm={10}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Payment Term ${idx + 1}`}
                    placeholder="e.g. 30 % interest-free advance along with the order."
                    value={pt.payment || ""}
                    onChange={e => handlePaymentTermChange(idx, e.target.value)}
                    multiline
                    maxRows={3}
                  />
                </Grid>
                <Grid item xs={2} sm={1} sx={{ textAlign: "center" }}>
                  <IconButton size="small" color="error" onClick={() => removePaymentTerm(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Box>
        </Box>
      </form>
    </Container>
  );
}